import { useEffect, useRef, useState, useMemo, lazy, Suspense } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import GlobeErrorBoundary from "@/components/GlobeErrorBoundary";

// Three.js / react-globe.gl loaded lazily — splits it into its own chunk so
// the initial JS bundle is ~4× smaller and the page becomes interactive faster
const LazyGlobe = lazy(() => import("react-globe.gl"));

const ROUTES = [
  { startLat: 52.52, startLng: 13.405, endLat: -34.6037, endLng: -58.3816 },
  { startLat: 40.4168, startLng: -3.7038, endLat: -34.6037, endLng: -58.3816 },
  { startLat: 41.9028, startLng: 12.4964, endLat: -34.6037, endLng: -58.3816 },
  { startLat: 48.8566, startLng: 2.3522, endLat: -34.6037, endLng: -58.3816 },
  { startLat: 38.7223, startLng: -9.1393, endLat: -34.6037, endLng: -58.3816 },
];

const CITY_COORDS = [
  { lat: 52.52, lng: 13.405, label: "BER" },
  { lat: 40.4168, lng: -3.7038, label: "MAD" },
  { lat: 41.9028, lng: 12.4964, label: "FCO" },
  { lat: 48.8566, lng: 2.3522, label: "CDG" },
  { lat: 38.7223, lng: -9.1393, label: "LIS" },
  { lat: -34.6037, lng: -58.3816, label: "EZE" },
];

const CENTER_LAT = 10;
const CENTER_LNG = -23;
const AMPLITUDE = 28;
const ALTITUDE = 2.2;
const BOUNCE_SPEED = 0.0032;

export default function Hero() {
  const { content } = useLanguage();
  const { hero, globe } = content;
  const globeRef = useRef();
  const containerRef = useRef();
  const [dims, setDims] = useState({ w: 1200, h: 800 });
  const [globeReady, setGlobeReady] = useState(false);
  const reduce = useReducedMotion();

  const cityPoints = useMemo(
    () =>
      CITY_COORDS.map((city, index) => ({
        ...city,
        name: globe.cities[index] ?? city.label,
      })),
    [globe.cities],
  );

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setDims({
          w: Math.max(containerRef.current.clientWidth, 280),
          h: Math.max(containerRef.current.clientHeight, 420),
        });
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (!globeReady || !globeRef.current) return;
    const g = globeRef.current;
    g.pointOfView({ lat: CENTER_LAT, lng: CENTER_LNG, altitude: ALTITUDE }, 0);
    const controls = g.controls();
    controls.autoRotate = false;
    controls.enableZoom = false;
    controls.enablePan = false;
    controls.enableRotate = false;
    controls.enableDamping = false;

    if (reduce) return;

    let t = Math.PI / 2;
    let rafId;
    function animate() {
      t += BOUNCE_SPEED;
      const lng = CENTER_LNG + AMPLITUDE * Math.cos(t);
      g.pointOfView({ lat: CENTER_LAT, lng, altitude: ALTITUDE }, 0);
      rafId = requestAnimationFrame(animate);
    }
    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [reduce, dims, globeReady]);

  const bp = hero.boardingPass;

  return (
    <section
      ref={containerRef}
      className="relative w-full min-h-[100svh] sm:min-h-[min(100svh,920px)] hero-dark overflow-hidden"
      data-testid="hero-section"
    >
      {/* Globe — lazy loaded so Three.js arrives in its own chunk, not blocking initial paint */}
      <div className="absolute inset-0 z-0 globe-container">
        <GlobeErrorBoundary>
          <Suspense fallback={null}>
            <LazyGlobe
              ref={globeRef}
              onGlobeReady={() => setGlobeReady(true)}
              width={dims.w}
              height={dims.h}
              backgroundColor="rgba(0,0,0,0)"
              globeImageUrl="https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
              showAtmosphere={true}
              atmosphereColor="rgba(37,99,235,0.55)"
              atmosphereAltitude={0.18}
              arcsData={ROUTES}
              arcColor={() => ["rgba(34,211,238,0.95)", "rgba(37,99,235,0.45)"]}
              arcAltitude={0.32}
              arcStroke={0.45}
              arcDashLength={0.4}
              arcDashGap={1.2}
              arcDashAnimateTime={3200}
              pointsData={cityPoints}
              pointLat="lat"
              pointLng="lng"
              pointColor={() => "#22D3EE"}
              pointAltitude={0.01}
              pointRadius={0.32}
              labelsData={cityPoints}
              labelLat="lat"
              labelLng="lng"
              labelText="name"
              labelSize={0.7}
              labelDotRadius={0.25}
              labelColor={() => "rgba(241,245,249,0.92)"}
              labelResolution={2}
              labelAltitude={0.02}
            />
          </Suspense>
        </GlobeErrorBoundary>
      </div>

      <div className="light-beam" />
      <div className="absolute inset-0 z-10 fog-vignette" />
      <div
        className="pointer-events-none absolute inset-0 z-[15] bg-gradient-to-b from-[#0D1B2A]/75 via-[#0D1B2A]/35 to-[#0D1B2A]/10"
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, x: 60, rotate: 8 }}
        animate={{ opacity: 1, x: 0, rotate: 4 }}
        transition={{ delay: 1.2, duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute top-[16%] right-[4%] z-40 pointer-events-none"
        style={{ perspective: "1200px" }}
        data-testid="hero-boarding-pass"
      >
        <motion.div
          animate={reduce ? {} : { y: [0, -10, 0], rotate: [4, 6, 4] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="w-72 relative"
          style={{ transform: "rotateY(18deg) rotateX(-4deg)", transformStyle: "preserve-3d" }}
        >
          <div className="bg-white rounded-xl flex overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.45),0_8px_24px_rgba(37,99,235,0.25)] ring-1 ring-white/80">
            <div className="holo-sheen w-2" />
            <div className="flex-1 p-5">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm leading-tight text-[#0D1B2A] sm:text-base">{bp.flight}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-display text-sm leading-tight text-[#2563EB] sm:text-base">{bp.gate}</div>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <div>
                  <div className="font-display text-3xl text-[#0D1B2A]">{bp.fromCode}</div>
                  <div className="text-[9px] text-[#64748B] mt-1 uppercase tracking-wider">{bp.fromCity}</div>
                </div>
                <div className="flex-1 mx-3 relative">
                  <div className="border-t border-dashed border-[#2563EB]/40" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#2563EB]">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
                    </svg>
                  </div>
                </div>
                <div>
                  <div className="font-display text-3xl text-[#0D1B2A]">{bp.toCode}</div>
                  <div className="text-[9px] text-[#64748B] mt-1 uppercase tracking-wider">{bp.toCity}</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <div className="relative z-30 flex min-h-[100svh] flex-col items-center justify-start px-4 pb-28 pt-[max(6.5rem,env(safe-area-inset-top)+4.5rem)] text-center sm:min-h-[min(100svh,920px)] sm:px-6 sm:pb-28 sm:pt-28 md:px-12 lg:px-20 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="mb-7 inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 backdrop-blur-md"
          data-testid="hero-eyebrow"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
          <span className="text-xs font-medium text-white/85">{hero.eyebrow}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="font-display mx-auto max-w-5xl text-[clamp(1.85rem,6vw+0.75rem,3.75rem)] leading-[0.98] tracking-tighter text-white drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)] sm:text-5xl md:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          {hero.titleAccent}<span className="gradient-text">{hero.titleRest.split(/\s+/)[0]}</span>{hero.titleRest.includes(" ") ? hero.titleRest.slice(hero.titleRest.indexOf(" ")) : ""}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="font-body mx-auto mt-4 max-w-xl px-1 text-[0.95rem] leading-relaxed text-white/90 sm:mt-6 sm:text-lg md:max-w-2xl"
          data-testid="hero-subtext"
        >
          {hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.5 }}
          className="mt-8 flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4"
        >
          <a
            href="#services"
            className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg bg-[#2563EB] px-6 py-3.5 text-sm font-semibold text-white touch-manipulation transition-all duration-300 shadow-[0_8px_24px_rgba(37,99,235,0.4)] hover:bg-[#1d4fd8] hover:shadow-[0_10px_30px_rgba(37,99,235,0.55)] active:scale-[0.99] sm:w-auto sm:px-7"
            data-testid="hero-cta-primary"
          >
            {hero.ctaBuscar} →
          </a>
          <a
            href="#services"
            className="inline-flex min-h-[48px] w-full items-center justify-center rounded-lg border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white touch-manipulation backdrop-blur-md transition-all duration-300 hover:bg-white/15 hover:border-[#22D3EE]/50 sm:w-auto sm:px-7"
            data-testid="hero-cta-secondary"
          >
            {hero.ctaOfrecer}
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-8 grid grid-cols-2 gap-2 px-1 text-left text-xs text-white/80 sm:flex sm:flex-wrap sm:justify-center sm:gap-x-7 sm:gap-y-3 sm:text-center sm:text-sm"
          data-testid="hero-bullets"
        >
          {hero.trustBadges.map((b) => (
            <li key={b.label} className="flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-[#10B981] shrink-0">
                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {b.label}
            </li>
          ))}
        </motion.ul>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-24 left-1/2 hidden -translate-x-1/2 items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 sm:bottom-32 sm:flex"
          data-testid="hero-coords"
        >
          <span>{hero.coordsBerlin}</span>
          <span className="w-10 h-px bg-white/20" />
          <span>{hero.coordsBuenosAires}</span>
        </motion.div>
      </div>

      <div className="hero-fade" />
    </section>
  );
}
